import { useLingui } from '@lingui/react/macro';
import { useState } from 'react';
import type { ExtraAnnouncement } from '@/feature/announcements';
import { formatDisplayDateTime } from '@/utils/localeDateFormat';
import useAppStore from '../store/rootStore';

// TEMPORARY (A-2224): delete once the CA, LY and PA blocks are live.
const NOTICE_COUNTRIES = new Set(['ca', 'ly', 'pa']);
const DISMISSED_AT_KEY = 'dex-geo-block-notice-dismissed-at';
const RESHOW_INTERVAL_MS = 24 * 60 * 60 * 1000;
const blockAt = formatDisplayDateTime(Date.UTC(2026, 10, 20, 23, 59), {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'UTC',
  timeZoneName: 'short',
});

// localStorage accessors throw when the browser blocks site data — treat it as never dismissed.
function readDismissedAt(): number {
  try {
    return Number(localStorage.getItem(DISMISSED_AT_KEY));
  } catch {
    return 0;
  }
}

export function useGeoBlockAnnouncement(): ExtraAnnouncement | null {
  const { t, i18n } = useLingui();
  const enabled = useAppStore((s) => s.config?.features.ontarioBanAnnouncementEnabled ?? false);
  const userCountry = useAppStore((s) => s.config?.userCountry);
  const discordUrl = useAppStore((s) => s.config?.features.discordUrl);
  const [dismissedAt, setDismissedAt] = useState(readDismissedAt);

  if (!enabled || !userCountry || !NOTICE_COUNTRIES.has(userCountry) || Date.now() - dismissedAt < RESHOW_INTERVAL_MS) {
    return null;
  }
  const country = new Intl.DisplayNames([i18n.locale], { type: 'region' }).of(userCountry.toUpperCase());
  return {
    id: 'geo-block-notice',
    title: t`Important notice for users in ${country}`,
    content: t`Our services are no longer offered to users in ${country} for regulatory reasons effective ${blockAt}. If you are located in ${country}, please close your positions and withdraw funds before that date. If you missed that opportunity, please contact our Discord ${discordUrl}`,
    onClose: () => {
      try {
        localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
      } catch {
        // Storage may be blocked; the dismissal still holds for this session.
      }
      setDismissedAt(Date.now());
    },
  };
}
